import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Transaction } from "../shared/types";
import { useHideBalances } from "../context/HideBalancesContext";
import { maskAmount } from "../lib/hidden-amount";
import { addressLabel } from "../lib/address-label";
import { formatTxTime, kindStyle, shortAddr, txShowsAmount, txTitle } from "../lib/tx-display";
import { TxDetailPanel } from "./tx-detail-panel";

/** Compact single-line tx preview for account row (max 3 shown). Tap opens the tx detail. */
export function CompactTxPreview({ tx, explorerUrl, networkName }: { tx: Transaction; explorerUrl: string; networkName?: string }) {
  const { t } = useTranslation();
  const { hidden } = useHideBalances();
  const [open, setOpen] = useState(false);
  const isPending = !tx.confirmed;
  const isFailed = !!tx.failed;
  const kStyle = tx.kind ? kindStyle(tx.kind) : null;
  const label = isFailed
    ? t("tx.failed")
    : isPending
      ? t("tx.pending")
      : tx.kind
        ? txTitle(tx, t, hidden)
      : tx.isDeployment
        ? t("tx.deployed")
        : tx.isApprove
          ? t("tx.approved")
          : tx.isContractCall
            ? t("tx.contract")
            : tx.direction === "in" ? t("tx.received") : tx.direction === "out" ? t("tx.sent") : t("tx.self");
  const color = isFailed
    ? "text-red-400"
    : isPending
      ? "text-yellow-400"
      : kStyle
        ? kStyle.text
      : tx.isDeployment
        ? "text-purple-400"
        : tx.isApprove
          ? "text-blue-400"
          : tx.isContractCall
            ? "text-orange-400"
            : tx.direction === "in" ? "text-green-500" : "text-red-400";
  const dotColor = isFailed
    ? "bg-red-400"
    : isPending
      ? "bg-yellow-400"
      : kStyle
        ? kStyle.bg.replace("/10", "")
      : tx.isDeployment
        ? "bg-purple-400"
        : tx.isApprove
          ? "bg-blue-400"
          : tx.isContractCall
            ? "bg-orange-400"
            : tx.direction === "in" ? "bg-green-500" : "bg-red-400";
  const sign = tx.isDeployment || tx.isApprove || tx.kind ? "" : tx.direction === "in" ? "+" : tx.direction === "out" ? "-" : "";

  // For deployments, show contract address; for others show counterparty
  const counterparty = tx.isDeployment && tx.createdContract
    ? shortAddr(tx.createdContract)
    : tx.direction === "in"
      ? `${t("tx.from").toLowerCase()} ${addressLabel(tx.from) ?? shortAddr(tx.from)}`
      : `${t("tx.to").toLowerCase()} ${addressLabel(tx.to) ?? shortAddr(tx.to)}`;

  // Hide "0 ETH" for deployments and approvals — show gas cost isn't useful here
  const showAmount = !tx.isDeployment && !tx.isApprove && (!tx.kind || txShowsAmount(tx));

  // Truncate to 8 decimal places for compact display (e.g. 0.000344447119101489 → 0.00034444)
  const dotIdx = tx.formatted.indexOf(".");
  const shortAmount = dotIdx >= 0 && tx.formatted.length > dotIdx + 9
    ? tx.formatted.slice(0, dotIdx + 9)
    : tx.formatted;

  return (
    <>
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      className="w-full text-left flex items-center gap-2 pl-8 md:pl-12 pr-3 md:pr-5 py-1.5 hover:bg-surface-tertiary/30 transition-colors"
    >
      {/* Direction dot */}
      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />

      {/* Label + counterparty */}
      <span className="text-[11px] text-text-muted truncate flex-1">
        <span className={`font-medium ${color}`}>{label}</span>
        <span className="text-text-muted/60 ml-1.5">{counterparty}</span>
      </span>

      {/* Amount + time */}
      {showAmount && (
        <span className={`text-[11px] tabular-nums font-medium shrink-0 ${color}`}>
          {hidden ? "" : sign}{maskAmount(shortAmount, tx.symbol, hidden)}
        </span>
      )}
      <span className="text-[10px] text-text-muted/50 shrink-0 w-12 text-right">
        {formatTxTime(tx.timestamp, t)}
      </span>
    </button>
    {open && <TxDetailPanel tx={tx} explorerUrl={explorerUrl} networkName={networkName} onClose={() => setOpen(false)} />}
    </>
  );
}
